import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { inboundProjectsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import { FiPlus, FiSearch, FiFilter, FiUpload, FiEye, FiEdit, FiTrash2, FiPackage } from 'react-icons/fi';
import { customerAPI } from '../../services/api';

const defaultServiceCapabilities = {
  inbound_project: true
};

const InboundProjects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    page: 1,
    limit: 10
  });

  // Fetch inbound projects
  const { 
    data: projectsData, 
    isLoading, 
    error, 
    refetch 
  } = useQuery(
    ['inboundProjects', filters], 
    () => inboundProjectsAPI.getProjects(filters),
    {
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
    }
  );

  const { data: customerProfileResponse } = useQuery(
    ['customerProfileInboundProjectList'],
    () => customerAPI.getProfile(),
    {
      staleTime: 300000
    }
  );

  const serviceCapabilities = {
    ...defaultServiceCapabilities,
    ...(customerProfileResponse?.data?.serviceCapabilities || customerProfileResponse?.data?.company?.service_capabilities || {})
  };
  const canCreateManifest = serviceCapabilities.inbound_project !== false;

  const projects = projectsData?.projects || [];
  const pagination = projectsData?.pagination || {};

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ 
      ...prev, 
      [key]: value,
      page: key !== 'page' ? 1 : value
    }));
  };

  // Status color mapping
  const getStatusColor = (status) => {
    const statusColors = {
      'draft': 'bg-gray-100 text-gray-800',
      'uploaded': 'bg-blue-100 text-blue-800',
      'mapped': 'bg-yellow-100 text-yellow-800',
      'validated': 'bg-green-100 text-green-800',
      'submitted': 'bg-purple-100 text-purple-800',
      'approved': 'bg-indigo-100 text-indigo-800',
      'partially_received': 'bg-orange-100 text-orange-800',
      'received': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inbound Manifests</h1>
          <p className="text-gray-600">Manage your expected incoming inventory</p>
        </div>
        {canCreateManifest && (
          <Link
            to="/customer/inbound-projects/new"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg inline-flex items-center space-x-2 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            <span>Create Manifest</span>
          </Link>
        )}
      </div>

      {/* Filters */}
      {!canCreateManifest && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Inbound manifests are not enabled for your company. Existing manifests remain visible, but new ones cannot be created.
        </div>
      )}
      <div className="bg-white p-4 rounded-lg shadow border">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="flex-1 min-w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Manifests
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search by name, code, or vendor..."
                className="pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="uploaded">Uploaded</option>
              <option value="mapped">Mapped</option>
              <option value="validated">Validated</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="partially_received">Partially Received</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Search Button */}
          <button
            type="submit"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md inline-flex items-center space-x-2 transition-colors"
          >
            <FiSearch className="w-4 h-4" />
            <span>Search</span>
          </button>
        </form>
      </div>

      {/* Projects List */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading projects: {error.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-white rounded-lg shadow border p-8 text-center">
          <FiPackage className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No inbound manifests found</h3>
          <p className="text-gray-600 mb-4">
                          {filters.search || filters.status 
                ? "No manifests match your current filters." 
                : "Get started by creating your first inbound manifest."
              }
          </p>
          {!filters.search && !filters.status && canCreateManifest && (
            <Link
              to="/customer/inbound-projects/new"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors"
            >
              <FiPlus className="w-4 h-4 mr-2" />
              Create Manifest
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manifest
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Warehouse
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expected Arrival
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {project.name || 'Unnamed Project'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {project.project_code && `Code: ${project.project_code}`}
                          {project.vendor && (
                            <span className="block">Vendor: {project.vendor}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                        {project.status?.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.DestinationWarehouse?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(project.expected_arrival)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {project.stats && (
                        <div className="text-sm">
                          <div className="text-gray-900">
                            {project.stats.totalReceived || 0} / {project.stats.totalExpected || 0} items
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                            <div 
                              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${project.stats.completionPercentage || 0}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {project.stats.completionPercentage || 0}% complete
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(project.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/customer/inbound-projects/${project.id}`}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="View Details"
                        >
                          <FiEye className="w-4 h-4" />
                        </Link>
                        {(project.status === 'draft' || project.status === 'uploaded') && (
                          <Link
                            to={`/customer/inbound-projects/${project.id}/edit`}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Edit Project"
                          >
                            <FiEdit className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                onPageChange={(page) => handleFilterChange('page', page)}
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InboundProjects; 
